/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 重要課題管理ページ。GM が課題を登録・編集・並び替え・進捗管理し、経営層が閲覧する。
 * @graph-connects useKeyIssues [calls] 課題一覧取得
 * @graph-connects useCreateKeyIssue [calls] 課題作成
 * @graph-connects useUpdateKeyIssue [calls] 課題更新
 * @graph-connects useDeleteKeyIssue [calls] 課題削除
 * @graph-connects useReorderKeyIssues [calls] 並び替え
 * @graph-connects usePermissions [calls] 権限チェック
 */

import { GripVertical, Plus, X } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { usePermissions } from '../hooks/usePermissions.js';
import {
  useKeyIssues,
  useCreateKeyIssue,
  useUpdateKeyIssue,
  useDeleteKeyIssue,
  useReorderKeyIssues,
} from '../hooks/useKeyIssues.js';
import type { KeyIssue, KeyIssueInput, KeyIssueStatus } from '../types/key-issue.js';

/** @graph-connects none */
const STATUS_CONFIG: Record<KeyIssueStatus, { labelKey: string; color: string; bg: string }> = {
  not_started: {
    labelKey: 'keyIssues.statusNotStarted',
    color: 'var(--color-text-muted)',
    bg: 'var(--color-bg-muted)',
  },
  in_progress: {
    labelKey: 'keyIssues.statusInProgress',
    color: 'var(--color-accent-primary)',
    bg: 'color-mix(in srgb, var(--color-accent-primary) 12%, transparent)',
  },
  done: {
    labelKey: 'keyIssues.statusDone',
    color: 'var(--color-success)',
    bg: 'color-mix(in srgb, var(--color-success) 12%, transparent)',
  },
};

/** @graph-connects none */
const NEXT_STATUS: Record<KeyIssueStatus, KeyIssueStatus> = {
  not_started: 'in_progress',
  in_progress: 'done',
  done: 'not_started',
};

/** @graph-connects none */
function getDueDateStyle(dueDate: string | null, status: KeyIssueStatus): string {
  if (!dueDate || status === 'done') return '';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(`${dueDate}T00:00:00`);
  if (due < today) return 'text-[var(--color-error)] font-semibold';
  if (due.getTime() === today.getTime()) return 'text-[var(--color-warning)] font-semibold';
  return '';
}

/** @graph-connects none */
function formatDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-');
  return `${y}/${m}/${d}`;
}

/** @graph-connects none */
export function KeyIssuesPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useKeyIssues();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('permissions-admin');

  const createMutation = useCreateKeyIssue();
  const updateMutation = useUpdateKeyIssue();
  const deleteMutation = useDeleteKeyIssue();
  const reorderMutation = useReorderKeyIssues();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingIssue, setEditingIssue] = useState<KeyIssue | null>(null);
  const dragSrcIdx = useRef<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const issues = data?.issues ?? [];

  const openCreateModal = useCallback(() => {
    setEditingIssue(null);
    setModalOpen(true);
  }, []);

  const openEditModal = useCallback(
    (issue: KeyIssue) => {
      if (!canEdit) return;
      setEditingIssue(issue);
      setModalOpen(true);
    },
    [canEdit],
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingIssue(null);
  }, []);

  const handleSave = useCallback(
    (input: KeyIssueInput) => {
      if (editingIssue) {
        updateMutation.mutate(
          { id: editingIssue.id, input },
          {
            onSuccess: () => {
              closeModal();
            },
          },
        );
      } else {
        createMutation.mutate(input, {
          onSuccess: () => {
            closeModal();
          },
        });
      }
    },
    [editingIssue, updateMutation, createMutation, closeModal],
  );

  const handleDelete = useCallback(() => {
    if (!editingIssue) return;
    deleteMutation.mutate(editingIssue.id, {
      onSuccess: () => {
        closeModal();
      },
    });
  }, [editingIssue, deleteMutation, closeModal]);

  const handleStatusCycle = useCallback(
    (issue: KeyIssue) => {
      if (!canEdit) return;
      const nextStatus = NEXT_STATUS[issue.status];
      updateMutation.mutate({
        id: issue.id,
        input: {
          title: issue.title,
          description: issue.description,
          status: nextStatus,
          dueDate: issue.dueDate,
        },
      });
    },
    [canEdit, updateMutation],
  );

  const handleDragStart = useCallback((idx: number) => {
    dragSrcIdx.current = idx;
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
    setDragOverIdx(idx);
  }, []);

  const handleDrop = useCallback(
    (targetIdx: number) => {
      const srcIdx = dragSrcIdx.current;
      if (srcIdx === null || srcIdx === targetIdx) {
        dragSrcIdx.current = null;
        setDragOverIdx(null);
        return;
      }
      const reordered = [...issues];
      const [moved] = reordered.splice(srcIdx, 1);
      if (moved) {
        reordered.splice(targetIdx, 0, moved);
        reorderMutation.mutate(reordered.map((i) => i.id));
      }
      dragSrcIdx.current = null;
      setDragOverIdx(null);
    },
    [issues, reorderMutation],
  );

  const handleDragEnd = useCallback(() => {
    dragSrcIdx.current = null;
    setDragOverIdx(null);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (isError) {
    return (
      <Card className="p-8 text-center text-[var(--color-text-muted)]">
        {t('common.fetchError')}
      </Card>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-[var(--color-text-primary)]">
          {t('keyIssues.title')}
        </h1>
        {canEdit && (
          <Button variant="primary" onClick={openCreateModal}>
            <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
            {t('keyIssues.addIssue')}
          </Button>
        )}
      </div>

      <Card className="overflow-hidden">
        {issues.length === 0 ? (
          <div className="py-16 text-center text-sm text-[var(--color-text-muted)]">
            {t('keyIssues.noIssues')}
          </div>
        ) : (
          <div>
            {issues.map((issue, idx) => {
              const st = STATUS_CONFIG[issue.status];
              const dueCls = getDueDateStyle(issue.dueDate, issue.status);
              return (
                <div
                  key={issue.id}
                  className={`flex items-center gap-3 border-b border-[var(--color-border)] px-4 py-3 last:border-b-0 transition-colors ${
                    dragOverIdx === idx
                      ? 'bg-[color-mix(in_srgb,var(--color-accent-primary)_8%,transparent)]'
                      : 'hover:bg-[var(--color-bg-muted)]'
                  }`}
                  draggable={canEdit}
                  onDragStart={() => {
                    handleDragStart(idx);
                  }}
                  onDragOver={(e) => {
                    handleDragOver(e, idx);
                  }}
                  onDrop={() => {
                    handleDrop(idx);
                  }}
                  onDragEnd={handleDragEnd}
                >
                  {canEdit && (
                    <div className="flex shrink-0 cursor-grab items-center text-[var(--color-text-muted)] active:cursor-grabbing">
                      <GripVertical className="h-4 w-4" aria-hidden="true" />
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      className="block w-full truncate text-left text-sm font-medium text-[var(--color-text-primary)] hover:underline"
                      onClick={() => {
                        openEditModal(issue);
                      }}
                      disabled={!canEdit}
                    >
                      {issue.title}
                    </button>
                    {issue.description && (
                      <p className="mt-0.5 truncate text-xs text-[var(--color-text-muted)]">
                        {issue.description}
                      </p>
                    )}
                  </div>

                  <button
                    type="button"
                    className="flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition-opacity hover:opacity-80"
                    style={{ color: st.color, backgroundColor: st.bg }}
                    onClick={() => {
                      handleStatusCycle(issue);
                    }}
                    disabled={!canEdit}
                  >
                    <span
                      className="inline-block h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: st.color }}
                    />
                    {t(st.labelKey)}
                  </button>

                  <span
                    className={`shrink-0 text-xs tabular-nums ${dueCls || 'text-[var(--color-text-secondary)]'}`}
                    style={{ minWidth: '5rem', textAlign: 'right' }}
                  >
                    {issue.dueDate ? formatDate(issue.dueDate) : '—'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {modalOpen && (
        <IssueModal
          issue={editingIssue}
          onSave={handleSave}
          onDelete={editingIssue ? handleDelete : undefined}
          onClose={closeModal}
          isSaving={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  );
}

interface IssueModalProps {
  issue: KeyIssue | null;
  onSave: (input: KeyIssueInput) => void;
  onDelete?: () => void;
  onClose: () => void;
  isSaving: boolean;
}

/** @graph-connects none */
function IssueModal({ issue, onSave, onDelete, onClose, isSaving }: IssueModalProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(issue?.title ?? '');
  const [description, setDescription] = useState(issue?.description ?? '');
  const [status, setStatus] = useState<KeyIssueStatus>(issue?.status ?? 'not_started');
  const [dueDate, setDueDate] = useState(issue?.dueDate ?? '');

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (!title.trim()) return;
    onSave({
      title: title.trim(),
      description: description.trim(),
      status,
      dueDate: dueDate || null,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div className="my-auto w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] shadow-xl">
        <div className="flex items-center justify-between px-5 pt-5">
          <h2 className="text-base font-bold text-[var(--color-text-primary)]">
            {issue ? t('keyIssues.editIssue') : t('keyIssues.addIssue')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-muted)] hover:text-[var(--color-text-primary)]"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 pb-5 pt-4">
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {t('keyIssues.titleLabel')}
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                }}
                className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent-primary)] focus:ring-2 focus:ring-[var(--color-accent-primary)]/20"
                placeholder={t('keyIssues.titlePlaceholder')}
                required
                autoFocus
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {t('common.detail')}
              </label>
              <textarea
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                }}
                className="w-full resize-y rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm leading-relaxed text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent-primary)] focus:ring-2 focus:ring-[var(--color-accent-primary)]/20"
                rows={3}
                placeholder={t('keyIssues.descPlaceholder')}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t('keyIssues.progress')}
                </label>
                <select
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value as KeyIssueStatus);
                  }}
                  className="w-full cursor-pointer rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent-primary)] focus:ring-2 focus:ring-[var(--color-accent-primary)]/20"
                >
                  <option value="not_started">{t('keyIssues.statusNotStarted')}</option>
                  <option value="in_progress">{t('keyIssues.statusInProgress')}</option>
                  <option value="done">{t('keyIssues.statusDone')}</option>
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t('keyIssues.dueDate')}
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => {
                    setDueDate(e.target.value);
                  }}
                  className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent-primary)] focus:ring-2 focus:ring-[var(--color-accent-primary)]/20"
                />
              </div>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between border-t border-[var(--color-border)] pt-4">
            <div>
              {onDelete && (
                <button
                  type="button"
                  onClick={onDelete}
                  className="text-sm font-medium text-[var(--color-error)] hover:underline"
                >
                  {t('common.delete')}
                </button>
              )}
            </div>
            <Button type="submit" variant="primary" disabled={isSaving || !title.trim()}>
              {isSaving ? t('keyIssues.saving') : t('common.save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
